import React, { useEffect, useRef, useState } from 'react';
import type { ContextModalProps } from '@mantine/modals';
import { Stack, Select, Button, Text } from '@mantine/core';
import { setLang } from '@/utils/i18n';
import useSettingsStorage from '@/hooks/useSettingsStorage';
import { getSpainRegionOptions } from '@/utils/holidays';
import { useTranslation } from 'react-i18next';

type InnerProps = {};

const InitialSetupContextModal = ({ context, id }: ContextModalProps<InnerProps>) => {
  const { save } = useSettingsStorage();
  const { t } = useTranslation();
  const [lang, setLangState] = useState<string | null>(null);
  const [region, setRegion] = useState<string | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // focus first control after mount
    const t = setTimeout(() => {
      if (!rootRef.current) {
        return;
      }
      const el = rootRef.current.querySelector<HTMLElement>('select, input, button');
      if (el) {
        try {
          el.focus();
        } catch (e) {
          // ignore
        }
      }
    }, 50);
    return () => clearTimeout(t);
  }, []);

  const handleSave = async () => {
    if (!lang || !region) {
      return;
    }
    try {
      // cast to allowed LangCode
      setLang(lang as 'es' | 'en' | 'bg' | 'ro');
    } catch (e) {
      // ignore
    }
    try {
      await save({ defaultRegion: region });
    } catch (e) {
      // ignore
    }
    context.closeModal(id);
  };

  return (
    <div ref={rootRef}>
      <Stack gap="md">
        <Text fw={700} size="lg">
          {t('initialSetup.title')}
        </Text>

        <Text size="sm" c="dimmed">
          {t('initialSetup.description')}
        </Text>
        <Select
          label={t('idiomaTitle')}
          placeholder={t('initialSetup.langPlaceholder')}
          value={lang ?? ''}
          onChange={(v) => setLangState(v || null)}
          data={[
            { value: 'es', label: t('lang.es') },
            { value: 'en', label: t('lang.en') },
            { value: 'bg', label: t('lang.bg') },
            { value: 'ro', label: t('lang.ro') },
          ]}
        />

        <Select
          label={t('defaultRegionTitle')}
          placeholder={t('initialSetup.regionPlaceholder')}
          value={region ?? ''}
          onChange={(v) => setRegion(v || undefined)}
          data={getSpainRegionOptions().map((r) => ({ value: r.value ?? '', label: r.label }))}
        />

        <Button fullWidth onClick={handleSave} disabled={!lang || !region} type="button">
          {t('initialSetup.saveButton')}
        </Button>
      </Stack>
    </div>
  );
};

export default InitialSetupContextModal;
